import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  addDoc,
  where,
  query,
} from 'firebase/firestore'
import { db } from '@/services/firebase'
import type { Institute, InstituteCreatePayload, InstituteUpdatePayload, UserRole } from '@/types'
import { omitUndefined } from '@/utils/objectUtils'

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}


/**
 * Fetch a single Institute document by ID from the `institutes` collection.
 *
 * Returns the Institute with its Firestore document ID merged in, or null
 * if no document exists. Firestore rules require the caller to be
 * authenticated and either a superuser or a member of the institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function getInstitute(id: string): Promise<Institute | null> {
  try {
    const snap = await getDoc(doc(db, 'institutes', id))
    if (!snap.exists()) return null
    return { id: snap.id, ...snap.data() } as Institute
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch all documents from the `institutes` collection without filtering.
 *
 * Returns every Institute document the authenticated user has read access to.
 * Firestore Security Rules restrict reads to superuser or users who appear
 * in the institute's `managerIds`, `tutorIds`, or `studentIds`.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listInstitutes(): Promise<Institute[]> {
  try {
    const snap = await getDocs(collection(db, 'institutes'))
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Institute)
  } catch (error) {
    throw createServiceError(error)
  }
}


/**
 * Retrieves a list of institutes based on the user's role and ID.
 *
 * @async
 * @function listInstituteByUser
 *
 * @param {UserRole} role - The role of the user requesting the list.
 *                          If the role is `'superuser'`, all institutes are returned.
 * @param {string} userId - The unique identifier of the user.
 *                          Used to filter institutes where the user is associated.
 *
 * @returns {Promise<Institute[]>} A promise that resolves to an array of {@link Institute} objects
 *                                 associated with the given user role and ID.
 *
 * @throws {ServiceError} Throws a service error if the query fails or an unexpected error occurs.
 *
 * @example
 * // Fetch institutes for a regular user
 * const institutes = await listInstituteByUser('admin', 'user123');
 *
 * @example
 * // Fetch all institutes for a superuser
 * const allInstitutes = await listInstituteByUser('superuser', 'superuser123');
 */
export async function listInstituteByUser(role: UserRole, userId: string): Promise<Institute[]> {
  try {
    if (role === 'superuser') return listInstitutes();

    const [managerSnap, tutorSnap, studentSnap] = await Promise.all([
      getDocs(query(collection(db, 'institutes'), where('managerIds', 'array-contains', userId))),
      getDocs(query(collection(db, 'institutes'), where('tutorIds', 'array-contains', userId))),
      getDocs(query(collection(db, 'institutes'), where('studentIds', 'array-contains', userId))),
    ])

    const byId = new Map<string, Institute>()
    for (const snap of [managerSnap, tutorSnap, studentSnap]) {
      for (const instituteDoc of snap.docs) {
        byId.set(instituteDoc.id, { id: instituteDoc.id, ...instituteDoc.data() } as Institute)
      }
    }

    return [...byId.values()]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Create a new Institute document in the `institutes` collection.
 *
 * Spreads the payload and stamps `createdAt` with `serverTimestamp()`.
 * Returns the auto-generated document ID. Only superusers can create
 * institutes — enforced by Firestore rules (`isSuperUser()`).
 *
 * Throws ServiceError on Firestore failure.
 */
export async function createInstitute(payload: InstituteCreatePayload): Promise<string> {
  try {
    const ref = await addDoc(collection(db, 'institutes'), omitUndefined({
      ...payload,
      createdAt: serverTimestamp(),
    }))
    return ref.id
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Update an existing Institute document by merging the given fields.
 *
 * Writes to `institutes/{id}` via `updateDoc`. Only supplied fields
 * are overwritten. Superuser and managers of this institute may update.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function updateInstitute(
  id: string,
  payload: InstituteUpdatePayload
): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', id), omitUndefined({
      ...payload,
    }))
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Delete an Institute document from the `institutes` collection by ID.
 *
 * Permanently removes the document. Only superusers can delete institutes.
 * Does not cascade — associated classes, units, and user memberships
 * remain and must be cleaned up separately.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function deleteInstitute(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'institutes', id))
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Add a tutor UID to the institute's `tutorIds` array via `arrayUnion`.
 *
 * Idempotent — adding an already-present UID is a no-op. Firestore rules
 * restrict this to superuser or managers of the institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function addTutorToInstitute(instituteId: string, tutorId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      tutorIds: arrayUnion(tutorId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Remove a tutor UID from the institute's `tutorIds` array via `arrayRemove`.
 *
 * Idempotent — removing an absent UID is a no-op. Firestore rules restrict
 * this to superuser or managers of the institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function removeTutorFromInstitute(
  instituteId: string,
  tutorId: string
): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      tutorIds: arrayRemove(tutorId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Add a student UID to the institute's `studentIds` array via `arrayUnion`.
 *
 * Idempotent — adding an already-present UID is a no-op. Firestore rules
 * restrict this to superuser or managers of the institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function addStudentToInstitute(
  instituteId: string,
  studentId: string
): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      studentIds: arrayUnion(studentId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Remove a student UID from the institute's `studentIds` array via `arrayRemove`.
 *
 * Idempotent — removing an absent UID is a no-op. Firestore rules restrict
 * this to superuser or managers of the institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function removeStudentFromInstitute(
  instituteId: string,
  studentId: string
): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      studentIds: arrayRemove(studentId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Add a unit ID to the institute's `unitIds` array via `arrayUnion`.
 *
 * Idempotent — adding an already-present ID is a no-op. Used when
 * creating a new unit to associate it with its institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function addUnitToInstitute(instituteId: string, unitId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      unitIds: arrayUnion(unitId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Remove a unit ID from the institute's `unitIds` array via `arrayRemove`.
 *
 * Idempotent — removing an absent ID is a no-op. Used when deleting a
 * unit to disassociate it from its institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function removeUnitFromInstitute(instituteId: string, unitId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      unitIds: arrayRemove(unitId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/** Add manager to institute (manager role can administrate an institute) */
export async function addManagerToInstitute(instituteId: string, managerId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      managerIds: arrayUnion(managerId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Remove a manager UID from the institute's `managerIds` array via `arrayRemove`.
 *
 * Idempotent — removing an absent UID is a no-op. Firestore rules restrict
 * this to superuser or managers of the institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function removeManagerFromInstitute(
  instituteId: string,
  managerId: string
): Promise<void> {
  try {
    await updateDoc(doc(db, 'institutes', instituteId), {
      managerIds: arrayRemove(managerId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

// Re-export setDoc for use in user creation
export { setDoc, doc }
